<template>
    <div style="width: 100%; height: 100%">
        <common-title-panel v-if="showTitle" :title="title" :subTitle="subTitle" />
        <div ref="chart" :style="{ width: '100%', height: chartHeight }"></div>
    </div>
</template>

<script>
import * as echarts from "echarts";
import CommonTitlePanel from "../panels/CommonTitlePanel.vue";

export default {
    props: {
        title: String,
        subTitle: String,
        chartHeight: {
            type: String,
            default: "100%",
        },
        data: Object,
        xAxisLabel: Array,
        showAxisLabel: {
            type: Boolean,
            default: true,
        },
        showTitle: {
            type: Boolean,
            default: true,
        },
        sideMargin: {
            type: Number,
        },
        showZeb: {
            type: Boolean,
            default: true,
        },
        bottomMargin: {
            type: Number,
            default: 70,
        },
        leftMargin: {
            type: Number,
        },
        rightMargin: {
            type: Number,
        },
        certifiedZEBGrade: String,
    },
    components: { CommonTitlePanel },
    data() {
        return {
            chart: null,
            efficiencyLevels: ["1+++", "1++", "1+", "1", "2", "3", "4", "5", "6", "7"],
            zebLevels: ["ZEB Plus", "ZEB1", "ZEB2", "ZEB3", "ZEB4", "ZEB5"],
            color: {
                efficiency: "#4caf50",
                zeb: "#005BBB",
                effLineGreen: "#9eaf9f",
                zebLineBlue: "#6b7a94",
            },
        };
    },
    watch: {
        data: {
            handler(newVal) {
                this.chartData = newVal;
                this.initChart();
            },
            deep: true,
        },
    },
    mounted() {
        this.initChart();
        this.$nextTick(() => {
            window.addEventListener("resize", this.resizeChart);
        });
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.resizeChart);
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
    },
    destroyed() {},
    methods: {
        initChart() {
            if (!this.data) return;
            this.chart = echarts.init(this.$refs.chart);

            const zebGrade = this.data.zeb.chartData.zebGrade;
            const efficiencyGrade = this.data.efficiency.chartData.efficiencyGrade;
            const labels = this.data.efficiency.labels;
            // const totalOperatingZEBGrade = this.data.zeb.totalOperatingZEBGrade; // "ZEB5"
            const certificateZEBGrade = this.certifiedZEBGrade;
            const certificateEfficiencyGrade = this.data.efficiency.certifiedGrade;

            // console.log("certificateZEBGrade", certificateZEBGrade);
            // console.log("certificateEfficiencyGrade", certificateEfficiencyGrade);
            // const totalOperatingGrade = this.data.efficiency.totalOperatingGrade; // "2"

            const convertToIndex = (value, list) => (value !== null ? list.indexOf(value) : null);

            const efficiencyIndexData = efficiencyGrade.map((g) => convertToIndex(g, this.efficiencyLevels));
            const zebIndexData = zebGrade.map((g) => convertToIndex(g, this.zebLevels));

            // console.log("efficiencyIndexData zebIndexData", efficiencyIndexData, zebIndexData);
            const option = {
                tooltip: {
                    trigger: "axis",
                    axisPointer: { type: "line" },
                    formatter: (params) => {
                        let result = `<div>${params[0].axisValue}</div>`;

                        params.forEach((item) => {
                            const grade =
                                item.seriesName === "건축물 에너지효율 등급"
                                    ? this.efficiencyLevels[item.data]
                                    : this.zebLevels[item.data];

                            result += `
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span>${item.marker} ${item.seriesName}</span>
          <span style="font-weight: bold;">${grade ?? "-"}</span>
        </div>`;
                        });

                        return result;
                    },
                },

                legend: {
                    bottom: 0,
                    data: ["건축물 에너지효율 등급", "제로 에너지 건축물 등급"],
                },
                grid: {
                    top: 30,
                    left: this.leftMargin ? `${this.leftMargin}%` : "12%",
                    right: this.rightMargin ? `${this.rightMargin}%` : "12%",
                    bottom: this.bottomMargin,
                },
                xAxis: {
                    type: "category",
                    data: labels,
                },
                yAxis: [
                    {
                        type: "category",
                        name: this.showAxisLabel ? "에너지효율 등급" : "",
                        data: this.efficiencyLevels,
                        inverse: true,
                        nameGap: 25,
                        axisLine: { show: false },
                        axisTick: { show: false },
                        axisLabel: { show: true },
                        splitLine: {
                            show: true,
                            lineStyle: {
                                color: "#e0e0e0",
                                type: "solid",
                            },
                        },
                    },
                    ...(this.showZeb
                        ? [
                              {
                                  type: "category",
                                  name: this.showAxisLabel ? "제로에너지 등급" : "",
                                  data: this.zebLevels,
                                  inverse: true,
                                  nameGap: 25,
                                  axisLine: { show: false },
                                  axisTick: { show: false },
                                  axisLabel: { show: true },
                                  splitLine: { show: false },
                              },
                          ]
                        : []),
                ],

                series: [
                    {
                        name: "건축물 에너지효율 등급",
                        type: "line",
                        yAxisIndex: 0,
                        data: efficiencyIndexData,
                        label: { show: true },
                        itemStyle: { color: this.color.efficiency },
                        lineStyle: { type: "dashed" },
                        markLine: {
                            symbol: "none",
                            label: {
                                show: true,
                                formatter: (param) => param.name,
                                position: "insideEndTop",
                                fontSize: 12,
                                color: this.color.effLineGreen,
                            },
                            lineStyle: {
                                type: "dashed",
                                color: this.color.effLineGreen,
                                width: 1,
                            },
                            data: [
                                {
                                    yAxis: this.efficiencyLevels.indexOf(certificateEfficiencyGrade),
                                    name: "에너지 효율 인증 등급",
                                },
                            ],
                        },
                    },
                    ...(this.showZeb
                        ? [
                              {
                                  name: "제로 에너지 건축물 등급",
                                  type: "line",
                                  yAxisIndex: 1,
                                  data: zebIndexData,
                                  label: { show: true },
                                  itemStyle: { color: this.color.zeb },
                                  lineStyle: { type: "dashed" },
                                  markLine: {
                                      symbol: "none",
                                      label: {
                                          show: true,
                                          formatter: (param) => param.name,
                                          position: "insideEndTop",
                                          fontSize: 12,
                                          color: this.color.zebLineBlue,
                                      },
                                      lineStyle: {
                                          type: "dashed",
                                          color: this.color.zebLineBlue,
                                          width: 1,
                                      },
                                      data: [
                                          {
                                              yAxis: this.zebLevels.indexOf(certificateZEBGrade),
                                              name: "제로 에너지 인증 등급",
                                          },
                                      ],
                                  },
                              },
                          ]
                        : []),
                ],
            };

            this.chart.setOption(option);
        },
        resizeChart() {
            this.chart?.resize();
        },
    },
};
</script>

<style scoped>
.chart-container {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
}
</style>
